import type { LoginResponse } from '@contracts/authentication/LoginResponse';
import type { MeResponse } from '@contracts/authentication/MeResponse';
import { AuthenticatedClientUser } from '@src/authentication/AuthenticatedClientUser';
import { AuthenticatedUser } from '@src/authentication/AuthenticatedUser';
import { AuthenticationException } from '@src/authentication/exceptions/AuthenticationException';
import { MustChangePasswordException } from '@src/authentication/exceptions/MustChangePasswordException';
import { AuthenticationStorage } from '@src/common/AuthenticationStorage';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { Client } from '@src/common/services/Client';
import { isAxiosError } from 'axios';

export interface LoginForm {
  email: string;
  password: string;
}

export const AuthenticationClient = {
  me: async () => {
    try {
      const response = await AuthenticatedClient.get<MeResponse>('me');
      if (response.data.type === 'User') {
        return new AuthenticatedUser({
          id: response.data.id,
          name: response.data.name,
          email: response.data.email,
          permissions: response.data.permissions,
        });
      }
      return new AuthenticatedClientUser({
        id: response.data.id,
        email: response.data.email,
        profiles: response.data.profiles,
      });
    } catch {
      return null;
    }
  },

  login: async (loginForm: LoginForm) => {
    try {
      const response = await Client.post<
        LoginResponse | { requiredAction: 'string' }
      >('login', loginForm, { withCredentials: true });
      if ('requiredAction' in response.data) {
        throw new MustChangePasswordException('Must change password');
      }

      AuthenticationStorage.setAccessToken(response.data.accessToken);
    } catch (e) {
      if (isAxiosError<{ error: string; message: string }>(e)) {
        if (
          e.status === 400 &&
          e.response?.data.error &&
          e.response?.data.message
        ) {
          throw new AuthenticationException(
            e.response.data.message,
            e.response.data.error,
          );
        }
      }
      throw e;
    }
  },

  logout: async () => {
    await AuthenticatedClient.post('logout');
    AuthenticationStorage.removeAccessToken();
  },

  forgotPassword: async (email: string) => {
    try {
      await Client.post('/auth/forgot-password', { email });
    } catch (e) {
      if (isAxiosError<{ error: string; message: string }>(e)) {
        throw new Error(
          e.response?.data.message || 'Failed to send reset code',
        );
      }
      throw e;
    }
  },
};
